<script lang="ts" setup>
import type { DataSourceEntry, ProductListingFilter } from "@/types";

type QueryValue = string | null;

const { preFilters } = defineProps<{ preFilters: ProductListingFilter }>();
const emit = defineEmits(["filter"]);

const route = useRoute();

const items = await useProductFilter(preFilters);
const allSelectedItems = items
  .map(({ filters }) => {
    return filters;
  })
  .flat();

const selected = ref<DataSourceEntry[]>([]);

const isOpen = ref(false);

const isStringEqual = (
  string1: string | null,
  string2: string | null,
  absolute = true,
) => {
  if (!string1 || !string2) return false;

  return absolute
    ? string1.toLowerCase() === string2.toLowerCase()
    : string1.toLowerCase().includes(string2.toLowerCase());
};

const filterWithQueryParams = (
  filters: DataSourceEntry[],
  queryValue: QueryValue | QueryValue[],
) => {
  if (!queryValue) return filters;

  return filters.filter(({ value, key }) => {
    if (Array.isArray(queryValue)) {
      return queryValue.some((item) => isStringEqual(value, item));
    }

    return isStringEqual(value, queryValue, key !== "price");
  });
};

onMounted(() => {
  const { design, price, type } = route.query;

  if (!design && !price && !type) {
    selected.value = allSelectedItems;
    emit("filter", []);
    return;
  }

  const selectedDesigns = filterWithQueryParams(
    allSelectedItems.filter(({ key }) => {
      return key === "designFeatures" || key === "backgroundColor";
    }),
    design,
  );

  const selectedPrices = filterWithQueryParams(
    allSelectedItems.filter(({ key }) => key === "price"),
    price,
  );

  const selectedTypes = filterWithQueryParams(
    allSelectedItems.filter(({ key }) => key === "type"),
    type,
  );

  // const selectedUses = filterWithQueryParams(
  //   allSelectedItems.filter(({ key }) => key === "use"),
  //   use,
  // );

  selected.value = [
    ...selectedDesigns,
    ...selectedPrices,
    ...selectedTypes,
    // ...selectedUses,
  ];

  emit("filter", selected.value);
});

const toggleOpen = () => {
  isOpen.value = !isOpen.value;
};

const toggleSelect = (item: any) => {
  if (selected.value.includes(item)) {
    const index = selected.value.indexOf(item);
    const newItems = [...selected.value];
    newItems.splice(index, 1);
    selected.value = newItems;
  } else {
    selected.value = [...selected.value, item];
  }
};

const selectAll = (label: string) => {
  const itemsToSelect = items.find((item) => item.label === label);
  if (!itemsToSelect) return;

  const isAllSelected = itemsToSelect.filters.every((item) =>
    selected.value.includes(item),
  );

  if (isAllSelected) {
    itemsToSelect.filters.forEach((item) => toggleSelect(item));
  } else {
    itemsToSelect.filters.forEach((filter: any) => {
      if (!selected.value.includes(filter)) {
        selected.value = [...selected.value, filter];
      }
    });
  }
};

const onResetFilter = () => {
  selected.value = allSelectedItems;
};

const onFilter = () => {
  toggleOpen();
  emit("filter", selected.value);
};
</script>

<template>
  <div class="py-5" :class="{ hidden: items.length === 0 }">
    <button
      type="button"
      class="flex gap-6 py-5 px-12 bg-[#AFA39D] rounded-full items-center"
      :class="{ 'bg-black text-white': isOpen }"
      @click="toggleOpen"
    >
      <span>Refine</span>
      <UIcon name="i-heroicons-adjustments-horizontal" />
    </button>

    <div class="relative" :class="{ hidden: !isOpen }">
      <div
        class="p-0 lg:py-10 lg:px-14 ng-white flex flex-col lg:gap-10 absolute z-10 bg-white rounded-lg mt-2.5"
      >
        <div
          class="hidden lg:grid gap-10"
          :class="{
            'lg:grid-cols-3': items.length === 3,
            'lg:grid-cols-2': items.length === 2,
            'lg:grid-cols-1': items.length === 1,
          }"
        >
          <div
            v-for="item in items"
            :key="item.label"
            class="flex flex-col gap-2.5"
          >
            <div class="flex gap-2.5 items-center">
              <span class="font-helvetica-bold">{{ item.label }}</span>
              <span
                class="underline text-sm font-helvetica-bold cursor-pointer"
                @click="() => selectAll(item.label)"
                >Select all</span
              >
            </div>
            <div
              v-for="filter in item.filters"
              :key="filter.name"
              class="flex gap-2.5 items-center py-2.5 cursor-pointer self-start"
              @click="() => toggleSelect(filter)"
            >
              <IconCheckMark v-if="selected.includes(filter)" class="h-6 w-6" />
              <IconCheckMarkEmpty v-else class="h-6 w-6" />
              <span>{{ filter.name }}</span>
            </div>
          </div>
        </div>

        <UAccordion
          :items="items"
          class="lg:hidden rounded-[10px] overflow-hidden"
        >
          <template #default="{ item, open }">
            <div
              class="flex justify-between items-center p-5 text-lg cursor-pointer"
              :class="{
                'border-b border-b-[#999] bg-white': !open,
                'bg-black text-white': open,
              }"
            >
              <span>{{ item.label }}</span>
              <UIcon
                name="i-heroicons-chevron-down-20-solid"
                class="w-5 h-5 ms-auto transform transition-transform duration-200 shrink-0"
                :class="[open && 'rotate-180']"
              />
            </div>
          </template>

          <template #item="{ item, open }">
            <div
              class="text-[#1D1F22] md:px-5 pt-0 pb-5"
              :class="{
                'border-b border-b-[#999]': open,
              }"
            >
              <div class="flex gap-2.5 items-center py-2.5 px-5 cursor-pointer">
                <IconCheckMark />
                <span @click="() => selectAll(item.label)">Select all</span>
              </div>
              <div
                v-for="filter in item.filters"
                :key="filter.name"
                class="flex gap-2.5 items-center py-2.5 px-5 cursor-pointer"
                @click="() => toggleSelect(filter)"
              >
                <IconCheckMark
                  v-if="selected.includes(filter)"
                  class="h-6 w-6"
                />
                <IconCheckMarkEmpty v-else class="h-6 w-6" />
                <span>{{ filter.name }}</span>
              </div>
            </div>
          </template>
        </UAccordion>

        <div
          class="flex justify-end lg:gap-10 gap-5 self-stretch items-start p-5 lg:p-0"
        >
          <button
            class="bg-[#E1DDD9] md:py-5 md:px-12 px-8 py-3 rounded-full"
            @click="onResetFilter"
          >
            Reset
          </button>
          <button
            class="bg-black text-white md:py-5 md:px-12 px-8 py-3 rounded-full"
            @click="onFilter"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
